import type { Tile } from "./generals-board-model";
import type { GeneralsSimulation } from "./seed-generals-simulation";
import {
  boardTile,
  simulationIndex,
  simulationNeighbors,
  simulationColumns as cols,
  type GridPosition,
} from "./generals-board-grid";
import {
  simulationDestination,
  resolveSimulationMove,
} from "./generals-simulation-move";

function growGeneralsArmies({
  tiles,
  tickCount,
}: {
  readonly tiles: readonly Tile[];
  readonly tickCount: number;
}) {
  for (const tile of tiles) {
    if (tile.owner === -1) continue;

    if (
      tile.general ||
      (tile.castle ? tickCount % 2 === 0 : tickCount % 22 === 0)
    )
      tile.count += 1;
  }
}
function simulationFrontier(tiles: readonly Tile[]): GridPosition[] {
  const frontier: GridPosition[] = [];

  for (const [index, tile] of tiles.entries()) {
    if (tile.owner === -1 || tile.count < 2) continue;

    const position = { x: index % cols, y: Math.floor(index / cols) };
    const exposed = simulationNeighbors(position).some((neighbor) => {
      const other = boardTile({ tiles, index: simulationIndex(neighbor) });
      return !other.mountain && other.owner !== tile.owner;
    });

    if (exposed) frontier.push(position);
  }

  for (let i = frontier.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const first = frontier[i];
    const second = frontier[j];

    if (!first || !second)
      throw new RangeError("Simulation frontier is out of bounds");

    [frontier[i], frontier[j]] = [second, first];
  }

  return frontier;
}

export function tickGeneralsSimulation(
  simulation: GeneralsSimulation,
): boolean {
  simulation.tickCount++;
  const { tiles, tickCount } = simulation;
  growGeneralsArmies({ tiles, tickCount });
  const frontier = simulationFrontier(tiles);
  // A third of the frontier moves each tick, with at least twelve attempts.
  const acts = Math.min(
    frontier.length,
    Math.max(12, Math.ceil(frontier.length / 3)),
  );
  let generalFell = false;

  for (const position of frontier.slice(0, acts)) {
    const src = boardTile({ tiles, index: simulationIndex(position) });

    if (src.owner === -1 || src.count < 2) continue;

    const dst = simulationDestination({ tiles, src, ...position });

    if (dst && resolveSimulationMove({ src, dst })) generalFell = true;
  }

  return generalFell;
}
